import * as React from "react";
import {
    Button,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    MenuPopover,
    MenuTrigger,
    Spinner,
    makeStyles,
    mergeClasses,
    tokens,
} from "@fluentui/react-components";
import {
    ArrowRotateClockwiseRegular,
    ArrowUndoRegular,
    CheckmarkRegular,
    DeleteRegular,
    DocumentAddRegular,
} from "@fluentui/react-icons";
import {
    DndContext,
    KeyboardSensor,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
    type DragEndEvent,
} from "@dnd-kit/core";
import { SortableContext, rectSortingStrategy, sortableKeyboardCoordinates, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { PDFDocument } from "pdf-lib";
import { documentService } from "../../../services/documentService";
import { hoechstensMb, MAX_SPEICHERN_BYTES } from "../../../services/documentEditingService";
import { useWindowManager } from "../../windowManager/WindowManagerContext";
import { openDokumentAuswahl } from "../auswahl/DokumentAuswahlFenster";
import {
    HAUPT,
    SeitenFehler,
    baueDokument,
    seitenAusQuelle,
    type Drehung,
    type SeitenEintrag,
    type SeitenQuelle,
} from "./seitenOps";
import { EINFUEGBAR_ACCEPT, quelleAusDatei, quelleAusDokument } from "./seitenQuellenLaden";
import { erzeugeVorschauQuelle, type VorschauQuelle } from "./seitenVorschau";
import { leererZustand, seitenReducer } from "./seitenZustand";

/**
 * Seiten-Organisator des PDF-Editors: Vorschaubilder aller Seiten, per Ziehen
 * umsortieren, einzeln oder gewählt drehen/entfernen, Seiten aus KMVS-
 * Dokumenten oder vom PC einfügen (= zusammenführen).
 *
 * Gearbeitet wird nur an der Liste `SeitenEintrag[]`; das PDF entsteht erst
 * beim „Übernehmen“ (`baueDokument`). Der Editor lädt danach mit den neuen
 * Bytes neu — alle weiteren Markierungen beziehen sich dann auf die
 * endgültige Seitenfolge.
 */

/**
 * Dieselbe Grenze wie beim Speichern (T-379: 100 MB, `MAX_SPEICHERN_BYTES`) —
 * größere Fassungen lehnt der Server ab. Besser hier sagen als nach dem Klick
 * auf Speichern.
 */
const MAX_BYTES = MAX_SPEICHERN_BYTES;
const VORSCHAU_BREITE = 140;

const mb = (bytes: number) =>
    (bytes / 1024 / 1024).toLocaleString("de-DE", { minimumFractionDigits: 1, maximumFractionDigits: 1 });

const useStyles = makeStyles({
    root: {
        height: "100%",
        display: "flex",
        flexDirection: "column",
        backgroundColor: tokens.colorNeutralBackground3,
        outlineStyle: "none",
    },
    bar: {
        flexShrink: 0,
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
        backgroundColor: tokens.colorNeutralBackground1,
        display: "flex",
        alignItems: "center",
        gap: "4px",
        padding: "4px 8px",
        flexWrap: "wrap",
        minHeight: "32px",
    },
    titel: { fontWeight: tokens.fontWeightSemibold, paddingLeft: "4px", paddingRight: "4px" },
    anzahl: { color: tokens.colorNeutralForeground3, fontSize: "12px", paddingRight: "8px" },
    spacer: { flex: 1 },
    hint: {
        fontSize: "12px",
        color: tokens.colorNeutralForeground3,
        paddingLeft: "6px",
        paddingRight: "6px",
    },
    fehler: { color: tokens.colorPaletteRedForeground1 },
    raster: {
        flex: 1,
        minHeight: 0,
        overflow: "auto",
        padding: "16px",
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
        gap: "12px",
        alignContent: "start",
    },
    kachel: {
        position: "relative",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "4px",
        padding: "6px",
        borderRadius: tokens.borderRadiusMedium,
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        backgroundColor: tokens.colorNeutralBackground1,
        cursor: "grab",
        userSelect: "none",
        touchAction: "manipulation",
        outlineStyle: "none",
        ":hover .seiten-kachel-knoepfe": { opacity: 1 },
        ":focus-within .seiten-kachel-knoepfe": { opacity: 1 },
        ":focus-visible": { boxShadow: `0 0 0 2px ${tokens.colorStrokeFocus2}` },
    },
    gewaehlt: {
        border: `1px solid ${tokens.colorBrandStroke1}`,
        boxShadow: `0 0 0 1px ${tokens.colorBrandStroke1}`,
        backgroundColor: tokens.colorBrandBackground2,
        "& .seiten-kachel-knoepfe": { opacity: 1 },
    },
    ziehend: { opacity: 0.5, zIndex: 1 },
    /** Quadratischer Rahmen: auch um 90° gedreht passt das Bild hinein. */
    bildRahmen: {
        width: `${VORSCHAU_BREITE}px`,
        height: `${VORSCHAU_BREITE}px`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
    },
    bild: {
        maxWidth: "100%",
        maxHeight: "100%",
        boxShadow: tokens.shadow4,
        backgroundColor: "#fff",
        transitionProperty: "transform",
        transitionDuration: "150ms",
        pointerEvents: "none",
    },
    platzhalter: {
        width: "100px",
        height: `${VORSCHAU_BREITE}px`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "12px",
        color: tokens.colorNeutralForeground3,
        backgroundColor: tokens.colorNeutralBackground2,
        border: `1px dashed ${tokens.colorNeutralStroke2}`,
    },
    nummer: { fontSize: "12px", color: tokens.colorNeutralForeground2 },
    etikett: {
        maxWidth: "100%",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        fontSize: "11px",
        padding: "0 6px",
        borderRadius: tokens.borderRadiusSmall,
        backgroundColor: tokens.colorNeutralBackground4,
        color: tokens.colorNeutralForeground2,
    },
    knoepfe: {
        position: "absolute",
        top: "4px",
        right: "4px",
        display: "flex",
        gap: "2px",
        opacity: 0,
        transitionProperty: "opacity",
        transitionDuration: "100ms",
        backgroundColor: tokens.colorNeutralBackground1,
        borderRadius: tokens.borderRadiusMedium,
        boxShadow: tokens.shadow4,
    },
    versteckt: { display: "none" },
});

// ─── Kachel ─────────────────────────────────────────────────────────────────

const Kachel: React.FC<{
    eintrag: SeitenEintrag;
    nummer: number;
    gewaehlt: boolean;
    /** Name der Quelle bei fremden Seiten; bei Seiten dieses Dokuments null. */
    fremdName: string | null;
    letzte: boolean;
    ladeVorschau: (quelle: string, seite: number) => Promise<string>;
    onKlick: (e: React.MouseEvent) => void;
    onDrehen: () => void;
    onEntfernen: () => void;
}> = ({ eintrag, nummer, gewaehlt, fremdName, letzte, ladeVorschau, onKlick, onDrehen, onEntfernen }) => {
    const styles = useStyles();
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: eintrag.key });
    const rahmenRef = React.useRef<HTMLDivElement | null>(null);
    const [src, setSrc] = React.useState<string | null>(null);

    // Vorschaubild erst rendern, wenn die Kachel in Sicht kommt — ein
    // 200-Seiten-Bündel soll nicht 200 Canvas-Durchläufe auf einmal kosten.
    React.useEffect(() => {
        let aus = false;
        const laden = () => {
            ladeVorschau(eintrag.quelle, eintrag.seite)
                .then((url) => !aus && setSrc(url))
                .catch(() => undefined /* Platzhalter bleibt stehen */);
        };
        const el = rahmenRef.current;
        if (!el || typeof IntersectionObserver === "undefined") {
            laden();
            return () => {
                aus = true;
            };
        }
        const beobachter = new IntersectionObserver(
            (eintraege) => {
                if (eintraege.some((e) => e.isIntersecting)) {
                    beobachter.disconnect();
                    laden();
                }
            },
            { rootMargin: "200px" },
        );
        beobachter.observe(el);
        return () => {
            aus = true;
            beobachter.disconnect();
        };
    }, [eintrag.quelle, eintrag.seite, ladeVorschau]);

    const label = `Seite ${nummer}${fremdName ? ` aus ${fremdName}` : ""}${eintrag.drehung ? `, um ${eintrag.drehung}° gedreht` : ""}`;
    // Knöpfe in der Kachel dürfen weder die Auswahl ändern noch Ziehen starten.
    const halt = (e: React.SyntheticEvent) => e.stopPropagation();

    return (
        <div
            ref={setNodeRef}
            {...attributes}
            {...listeners}
            role="option"
            data-seiten-key={eintrag.key}
            aria-selected={gewaehlt}
            aria-label={label}
            title="Klicken oder Leertaste wählt aus (Strg/Umschalt für mehrere), Ziehen oder Enter + Pfeiltasten verschiebt"
            className={mergeClasses(styles.kachel, gewaehlt && styles.gewaehlt, isDragging && styles.ziehend)}
            style={{ transform: CSS.Transform.toString(transform), transition }}
            onClick={onKlick}
        >
            <div ref={rahmenRef} className={styles.bildRahmen}>
                {src ? (
                    <img
                        className={styles.bild}
                        src={src}
                        alt=""
                        draggable={false}
                        style={{ transform: eintrag.drehung ? `rotate(${eintrag.drehung}deg)` : undefined }}
                    />
                ) : (
                    <div className={styles.platzhalter} style={{ transform: eintrag.drehung ? `rotate(${eintrag.drehung}deg)` : undefined }}>
                        Seite {nummer}
                    </div>
                )}
            </div>
            <span className={styles.nummer}>{nummer}</span>
            {fremdName && (
                <span className={styles.etikett} title={fremdName}>
                    {fremdName}
                </span>
            )}
            <div className={mergeClasses(styles.knoepfe, "seiten-kachel-knoepfe")} onPointerDown={halt} onKeyDown={halt}>
                <Button
                    size="small"
                    appearance="subtle"
                    icon={<ArrowRotateClockwiseRegular />}
                    aria-label={`Seite ${nummer} drehen`}
                    title="Diese Seite um 90° im Uhrzeigersinn drehen (Taste R dreht alle gewählten)"
                    onClick={(e) => {
                        halt(e);
                        onDrehen();
                    }}
                />
                <Button
                    size="small"
                    appearance="subtle"
                    icon={<DeleteRegular />}
                    aria-label={`Seite ${nummer} entfernen`}
                    title={letzte ? "Mindestens eine Seite muss bleiben" : "Diese Seite entfernen (Taste Entf entfernt alle gewählten)"}
                    disabled={letzte}
                    onClick={(e) => {
                        halt(e);
                        onEntfernen();
                    }}
                />
            </div>
        </div>
    );
};

// ─── Organisator ────────────────────────────────────────────────────────────

type Lader = { name: string; laden: () => Promise<SeitenQuelle> };

export const SeitenOrganisator: React.FC<{
    docId: number;
    /** Aktueller Arbeitsstand inkl. Annotationen (aus pdf.saveDocument()). */
    bytes: Uint8Array;
    /** Drehung, die der Editor noch nicht geschrieben hat — wird auf alle Hauptseiten vorbelegt. */
    offeneDrehung: Drehung;
    onUebernehmen: (ergebnis: { bytes: Uint8Array; eintraege: SeitenEintrag[] }) => void;
    onAbbrechen: () => void;
}> = ({ docId, bytes, offeneDrehung, onUebernehmen, onAbbrechen }) => {
    const styles = useStyles();
    const { openWindow, closeWindow } = useWindowManager();
    // Offenes Auswahlfenster: verschwindet der Organisator (Abbrechen,
    // Editor zu), darf das Fenster nicht in eine tote Liste einfügen wollen.
    const auswahlFensterRef = React.useRef<string | null>(null);
    const closeWindowRef = React.useRef(closeWindow);
    closeWindowRef.current = closeWindow;
    const rootRef = React.useRef<HTMLDivElement | null>(null);
    const dateiRef = React.useRef<HTMLInputElement | null>(null);

    const [z, dispatch] = React.useReducer(seitenReducer, leererZustand);
    // Quellen wachsen nur (Rückgängig braucht sie weiter); als Ref, weil die
    // asynchronen Einfüge-Abläufe den aktuellen Stand brauchen.
    const quellenRef = React.useRef(new Map<string, SeitenQuelle>());
    const vorschauRef = React.useRef(new Map<string, VorschauQuelle>());
    const [bereit, setBereit] = React.useState(false);
    const [laedt, setLaedt] = React.useState(false);
    const [bauend, setBauend] = React.useState(false);
    const bauendRef = React.useRef(false);
    const [fehler, setFehler] = React.useState<string | null>(null);
    const [zuGross, setZuGross] = React.useState<number | null>(null);
    const [frage, setFrage] = React.useState(false);
    const aktivRef = React.useRef(true);

    React.useEffect(() => {
        aktivRef.current = true;
        const vorschauen = vorschauRef.current;
        return () => {
            aktivRef.current = false;
            if (auswahlFensterRef.current) closeWindowRef.current(auswahlFensterRef.current);
            auswahlFensterRef.current = null;
            for (const v of vorschauen.values()) v.schliessen();
            vorschauen.clear();
        };
    }, []);

    // Hauptdokument einlesen: Seitenzahl bestimmen, offene Drehung vorbelegen.
    React.useEffect(() => {
        let aus = false;
        setBereit(false);
        PDFDocument.load(bytes.slice(), { updateMetadata: false })
            .then((d) => {
                if (aus) return;
                const haupt: SeitenQuelle = { id: HAUPT, name: "Dieses Dokument", bytes, seitenzahl: d.getPageCount() };
                quellenRef.current = new Map([[HAUPT, haupt]]);
                for (const v of vorschauRef.current.values()) v.schliessen();
                vorschauRef.current.clear();
                dispatch({ typ: "start", eintraege: seitenAusQuelle(haupt).map((e) => ({ ...e, drehung: offeneDrehung })) });
                setBereit(true);
                rootRef.current?.focus();
            })
            .catch(() => !aus && setFehler("Die Seiten konnten nicht gelesen werden."));
        return () => {
            aus = true;
        };
        // offeneDrehung zählt nur beim Öffnen — spätere Änderungen gibt es nicht.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bytes]);

    // Jede Änderung macht eine frühere Größenwarnung hinfällig.
    React.useEffect(() => setZuGross(null), [z.eintraege]);

    const ladeVorschau = React.useCallback((quelle: string, seite: number) => {
        let v = vorschauRef.current.get(quelle);
        if (!v) {
            const q = quellenRef.current.get(quelle);
            if (!q) return Promise.reject(new Error("Quelle fehlt"));
            v = erzeugeVorschauQuelle(q.bytes);
            vorschauRef.current.set(quelle, v);
        }
        return v.seite(seite, VORSCHAU_BREITE);
    }, []);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        // Leertaste gehört der Auswahl; Ziehen per Tastatur startet mit Enter,
        // Pfeiltasten verschieben, Enter/Leertaste legt ab, Escape bricht ab.
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
            keyboardCodes: { start: ["Enter"], cancel: ["Escape"], end: ["Enter", "Space"] },
        }),
    );

    const onDragEnd = React.useCallback(
        ({ active, over }: DragEndEvent) => {
            if (!over || active.id === over.id) return;
            const keys = z.eintraege.map((e) => e.key);
            dispatch({ typ: "verschieben", von: keys.indexOf(String(active.id)), nach: keys.indexOf(String(over.id)) });
        },
        [z.eintraege],
    );

    const entfernen = React.useCallback(
        (keys: readonly string[]) => {
            if (keys.length === 0) return;
            const weg = new Set(keys);
            if (z.eintraege.every((e) => weg.has(e.key))) {
                setFehler("Mindestens eine Seite muss bleiben.");
                return;
            }
            dispatch({ typ: "entfernen", keys });
        },
        [z.eintraege],
    );

    /**
     * Quellen laden und einfügen. Fehlschläge einzelner Dateien (geschützt,
     * kein PDF/Bild) landen in der Hinweiszeile — die übrigen kommen trotzdem.
     */
    const einfuegen = React.useCallback(async (lader: Lader[]) => {
        // Während des Baus nichts mehr annehmen — das Ergebnis enthielte die
        // neuen Seiten nicht, der Nutzer sähe sie aber in der Liste.
        if (lader.length === 0 || bauendRef.current) return;
        setFehler(null);
        setLaedt(true);
        const neue: SeitenEintrag[] = [];
        const meldungen: string[] = [];
        for (const l of lader) {
            try {
                const q = await l.laden();
                quellenRef.current.set(q.id, q);
                neue.push(...seitenAusQuelle(q));
            } catch (e) {
                // Nur eigene, deutsche Meldungen zeigen — pdf-lib meldet englisch.
                meldungen.push(e instanceof SeitenFehler ? e.message : `Die Datei „${l.name}“ konnte nicht gelesen werden.`);
            }
        }
        if (!aktivRef.current) return;
        setLaedt(false);
        dispatch({ typ: "einfuegen", neue });
        if (meldungen.length) setFehler(meldungen.join(" "));
    }, []);

    const vomPc = React.useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const dateien = Array.from(e.target.files ?? []);
            // Leeren, damit dieselbe Datei gleich noch einmal gewählt werden kann.
            e.target.value = "";
            void einfuegen(dateien.map((f) => ({ name: f.name, laden: () => quelleAusDatei(f) })));
        },
        [einfuegen],
    );

    const ausKmvs = React.useCallback(async () => {
        setFehler(null);
        let contactId: number | null = null;
        try {
            // T-41: ohne eigenen Kontakt der Kunde von Vertrag/Schaden/Risiko/Vorgang.
            const doc = await documentService.getOne(docId);
            contactId = doc.contactId ?? doc.kundeId ?? null;
        } catch {
            /* Ohne Kunde zeigt die Auswahl den Kunden-Picker. */
        }
        if (!aktivRef.current) return;
        auswahlFensterRef.current = openDokumentAuswahl(openWindow, {
            contactId,
            accept: EINFUEGBAR_ACCEPT,
            ausschluss: new Set([docId]),
            uebernehmenText: "Einfügen",
            titel: "Seiten einfügen aus Dokument",
            onUebernehmen: ({ dokumente, dateien }) => {
                // Das Fenster schließt sich danach selbst.
                auswahlFensterRef.current = null;
                void einfuegen([
                    ...dokumente.map((d) => ({ name: d.fileName, laden: () => quelleAusDokument(d) })),
                    // Der Reiter „Rechner“ liefert Dateien vom PC gleich mit.
                    ...dateien.map((f) => ({ name: f.name, laden: () => quelleAusDatei(f) })),
                ]);
            },
        });
    }, [docId, einfuegen, openWindow]);

    const geaendert = z.verlauf.length > 0;

    const uebernehmen = React.useCallback(async () => {
        if (bauendRef.current) return;
        // Nichts geändert: kein Neuaufbau — der Editor bleibt, wie er war.
        if (!geaendert) {
            onAbbrechen();
            return;
        }
        bauendRef.current = true;
        setBauend(true);
        setFehler(null);
        try {
            const out = await baueDokument(quellenRef.current, z.eintraege);
            if (!aktivRef.current) return;
            if (out.byteLength > MAX_BYTES) {
                setZuGross(out.byteLength);
                return;
            }
            onUebernehmen({ bytes: out, eintraege: z.eintraege });
        } catch (e) {
            if (aktivRef.current) {
                setFehler(e instanceof SeitenFehler ? e.message : "Die Seiten konnten nicht zusammengestellt werden.");
            }
        } finally {
            bauendRef.current = false;
            if (aktivRef.current) setBauend(false);
        }
    }, [geaendert, onAbbrechen, onUebernehmen, z.eintraege]);

    const abbrechen = React.useCallback(() => {
        if (geaendert) setFrage(true);
        else onAbbrechen();
    }, [geaendert, onAbbrechen]);

    const onKeyDown = React.useCallback(
        (e: React.KeyboardEvent) => {
            const ziel = e.target as HTMLElement;
            // React reicht Tastendrücke aus Portalen (Einfüge-Menü) durch den
            // Komponentenbaum hoch — die gehören nicht zu den Seiten.
            if (!rootRef.current?.contains(ziel)) return;
            if (ziel.tagName === "INPUT" || ziel.tagName === "TEXTAREA" || ziel.isContentEditable) return;
            // Kachel mit Tastaturfokus: Leertaste wählt sie, und ohne Auswahl
            // wirken Entf/R auf sie — wie in Explorer und Acrobat.
            const fokusKey = ziel.closest("[data-seiten-key]")?.getAttribute("data-seiten-key") ?? null;
            const gewaehlt = z.eintraege.filter((x) => z.auswahl.has(x.key)).map((x) => x.key);
            const betroffen = gewaehlt.length ? gewaehlt : fokusKey ? [fokusKey] : [];
            if (fokusKey && (e.code === "Space" || e.key === " ")) {
                e.preventDefault();
                dispatch({
                    typ: "waehlen",
                    key: fokusKey,
                    art: e.shiftKey ? "bereich" : e.ctrlKey || e.metaKey ? "dazu" : "einzeln",
                });
            } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") {
                e.preventDefault();
                dispatch({ typ: "rueckgaengig" });
            } else if (e.key === "Delete" && betroffen.length) {
                e.preventDefault();
                entfernen(betroffen);
            } else if (e.key.toLowerCase() === "r" && !e.ctrlKey && !e.metaKey && !e.altKey && betroffen.length) {
                e.preventDefault();
                dispatch({ typ: "drehen", keys: betroffen });
            }
        },
        [entfernen, z.auswahl, z.eintraege],
    );

    const anzahl = z.eintraege.length;
    let hinweis: React.ReactNode = null;
    if (fehler) {
        hinweis = (
            <span className={mergeClasses(styles.hint, styles.fehler)} role="alert">
                {fehler}
            </span>
        );
    } else if (zuGross !== null) {
        hinweis = (
            <span className={mergeClasses(styles.hint, styles.fehler)} role="alert">
                Ergebnis ist {mb(zuGross)} MB — gespeichert werden {hoechstensMb(MAX_BYTES)}.
            </span>
        );
    } else if (laedt) {
        hinweis = <Spinner size="extra-tiny" label="Seiten werden geladen…" />;
    } else if (bereit) {
        hinweis = <span className={styles.hint}>Ziehen zum Umsortieren · Entf entfernt, R dreht die gewählten Seiten</span>;
    }

    return (
        <div ref={rootRef} className={styles.root} role="region" aria-label="Seiten" tabIndex={-1} onKeyDown={onKeyDown}>
            <div className={styles.bar}>
                <span className={styles.titel}>Seiten</span>
                <span className={styles.anzahl}>{bereit ? `${anzahl} ${anzahl === 1 ? "Seite" : "Seiten"}` : ""}</span>
                <Menu>
                    <MenuTrigger disableButtonEnhancement>
                        <MenuButton
                            appearance="subtle"
                            icon={<DocumentAddRegular />}
                            disabled={!bereit || laedt || bauend}
                            title="Seiten aus einem anderen Dokument oder vom PC einfügen — hinter der gewählten Seite, sonst am Ende"
                        >
                            Seiten einfügen
                        </MenuButton>
                    </MenuTrigger>
                    <MenuPopover>
                        <MenuList>
                            <MenuItem
                                onClick={() => void ausKmvs()}
                                title="Seiten aus einem Dokument einfügen, das schon in KMVS liegt (Akte des Kunden, Vorgänge)"
                            >
                                Aus KMVS-Dokument…
                            </MenuItem>
                            <MenuItem
                                onClick={() => dateiRef.current?.click()}
                                title="PDF, JPG oder PNG von diesem Rechner auswählen — Bilder werden zu je einer Seite"
                            >
                                Vom PC…
                            </MenuItem>
                        </MenuList>
                    </MenuPopover>
                </Menu>
                {/* Datei-Wähler, kein Eingabefeld — sichtbar ist nur der Menüeintrag. */}
                <input
                    ref={dateiRef}
                    type="file"
                    multiple
                    accept={EINFUEGBAR_ACCEPT}
                    className={styles.versteckt}
                    onChange={vomPc}
                    tabIndex={-1}
                    aria-hidden
                />
                <Button
                    appearance="subtle"
                    icon={<ArrowUndoRegular />}
                    onClick={() => dispatch({ typ: "rueckgaengig" })}
                    disabled={!geaendert || bauend}
                    title="Letzten Schritt zurücknehmen (Strg+Z)"
                >
                    Rückgängig
                </Button>

                <span className={styles.spacer} />
                {frage ? (
                    <>
                        <span className={styles.hint} role="alert">
                            Änderungen an den Seiten verwerfen?
                        </span>
                        <Button appearance="subtle" onClick={onAbbrechen} title="Seiten so lassen, wie sie vorher waren">
                            Verwerfen
                        </Button>
                        <Button appearance="primary" onClick={() => setFrage(false)} title="Zurück zu den Seiten">
                            Weiter bearbeiten
                        </Button>
                    </>
                ) : (
                    <>
                        {hinweis}
                        <Button appearance="subtle" onClick={abbrechen} disabled={bauend} title="Zurück zum Blatt, ohne die Seiten zu ändern">
                            Abbrechen
                        </Button>
                        <Button
                            appearance="primary"
                            icon={bauend ? <Spinner size="extra-tiny" /> : <CheckmarkRegular />}
                            onClick={() => void uebernehmen()}
                            disabled={!bereit || bauend || laedt || zuGross !== null}
                            title="Neue Seitenfolge übernehmen — gespeichert wird erst mit „Speichern“"
                        >
                            Übernehmen
                        </Button>
                    </>
                )}
            </div>

            {!bereit && !fehler ? (
                <div className={styles.raster}>
                    <Spinner size="small" label="Seiten werden gelesen…" />
                </div>
            ) : (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                    <SortableContext items={z.eintraege.map((e) => e.key)} strategy={rectSortingStrategy}>
                        <div className={styles.raster} role="listbox" aria-multiselectable aria-label="Seitenfolge">
                            {z.eintraege.map((e, i) => (
                                <Kachel
                                    key={e.key}
                                    eintrag={e}
                                    nummer={i + 1}
                                    gewaehlt={z.auswahl.has(e.key)}
                                    fremdName={e.quelle === HAUPT ? null : quellenRef.current.get(e.quelle)?.name ?? null}
                                    letzte={anzahl === 1}
                                    ladeVorschau={ladeVorschau}
                                    onKlick={(ev) =>
                                        dispatch({
                                            typ: "waehlen",
                                            key: e.key,
                                            art: ev.shiftKey ? "bereich" : ev.ctrlKey || ev.metaKey ? "dazu" : "einzeln",
                                        })
                                    }
                                    onDrehen={() => dispatch({ typ: "drehen", keys: [e.key] })}
                                    onEntfernen={() => entfernen([e.key])}
                                />
                            ))}
                        </div>
                    </SortableContext>
                </DndContext>
            )}
        </div>
    );
};
